import { useState } from 'react';
import { auth, db } from '../services/firebase';
import { RecaptchaVerifier, signInWithPhoneNumber, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

export default function LoginScreen({ onLogin }) {
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const createUserIfNew = async (uid, name, email) => {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      await setDoc(userRef, {
        name: name || 'Anonymous',
        email: email || '',
        coins: 500,
        createdAt: serverTimestamp(),
      });
    }
  };

  const loginWithGoogle = async () => {
    setLoading(true);
    setError('');
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await createUserIfNew(result.user.uid, result.user.displayName, result.user.email);
      onLogin(result.user.uid);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const sendOTP = async () => {
    if (phone.length < 10) { setError('Enter 10 digit phone number'); return; }
    setLoading(true); setError('');
    try {
      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
      }
      const result = await signInWithPhoneNumber(auth, '+91' + phone, window.recaptchaVerifier);
      setConfirmation(result);
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const verifyOTP = async () => {
    if (otp.length < 6) { setError('Enter 6 digit OTP'); return; }
    setLoading(true); setError('');
    try {
      const result = await confirmation.confirm(otp);
      await createUserIfNew(result.user.uid, '', '');
      onLogin(result.user.uid);
    } catch (e) { setError('Wrong OTP. Try again.'); }
    setLoading(false);
  };

  return (
    <div style={styles.container}>
      {/* Background circles */}
      <div style={styles.circle1}></div>
      <div style={styles.circle2}></div>

      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoContainer}>
          <div style={styles.logoIcon}>
            <span style={styles.logoEmoji}>💬</span>
          </div>
          <div style={styles.logoText}>
            <span style={styles.logoDe}>de</span>
            <span style={styles.logoLrn}>lrn</span>
          </div>
          <p style={styles.tagline}>Unlearn the fear. Speak German.</p>
        </div>

        {/* Google Login */}
        <button style={styles.googleBtn} onClick={loginWithGoogle} disabled={loading}>
          <span style={styles.googleG}>G</span>
          {loading ? 'Signing in...' : 'Continue with Google'}
        </button>

        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <span style={styles.dividerText}>or use phone</span>
          <div style={styles.dividerLine}></div>
        </div>

        {!confirmation ? (
          <>
            <div style={styles.inputRow}>
              <span style={styles.prefix}>+91</span>
              <input
                style={styles.input}
                type="tel"
                placeholder="Phone number"
                maxLength={10}
                value={phone}
                onChange={e => setPhone(e.target.value)}
              />
            </div>
            <div id="recaptcha-container"></div>
            <button style={styles.otpBtn} onClick={sendOTP} disabled={loading}>
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          </>
        ) : (
          <>
            <p style={styles.otpText}>OTP sent to +91{phone}</p>
            <input
              style={styles.otpInput}
              type="tel"
              placeholder="• • • • • •"
              maxLength={6}
              value={otp}
              onChange={e => setOtp(e.target.value)}
            />
            <button style={styles.otpBtn} onClick={verifyOTP} disabled={loading}>
              {loading ? 'Verifying...' : 'Verify & Login'}
            </button>
            <p style={styles.changeNumber} onClick={() => setConfirmation(null)}>
              Change number
            </p>
          </>
        )}

        {error && <p style={styles.error}>{error}</p>}
      </div>
    </div>
  );
}

const styles = {
  container: {
    backgroundColor: '#0a1628',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: "'Segoe UI', sans-serif",
  },
  circle1: {
    position: 'absolute',
    width: '400px',
    height: '400px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, #00d4a020, transparent)',
    top: '-100px',
    right: '-100px',
  },
  circle2: {
    position: 'absolute',
    width: '200px',
    height: '200px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, #00d4a015, transparent)',
    bottom: '-50px',
    left: '-50px',
  },
  card: {
    backgroundColor: '#0d2137',
    borderRadius: '24px',
    padding: '40px 32px',
    width: '100%',
    maxWidth: '400px',
    border: '1px solid #00d4a020',
    position: 'relative',
    zIndex: 1,
    margin: '0 auto',
  },
  logoContainer: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  logoIcon: {
    width: '64px',
    height: '64px',
    borderRadius: '20px',
    background: 'linear-gradient(135deg, #00d4a0, #00a87d)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 12px',
    boxShadow: '0 8px 24px #00d4a040',
  },
  logoEmoji: { fontSize: '32px' },
  logoText: {
    fontSize: '32px',
    fontWeight: '900',
    marginBottom: '8px',
  },
  logoDe: { color: '#0a1628', WebkitTextStroke: '2px #ffffff', color: '#ffffff' },
  logoLrn: { color: '#00d4a0' },
  tagline: {
    color: '#ffffff60',
    fontSize: '13px',
    margin: 0,
  },
  googleBtn: {
    width: '100%',
    backgroundColor: '#ffffff',
    border: 'none',
    borderRadius: '14px',
    padding: '14px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '20px',
    color: '#0a1628',
  },
  googleG: { fontSize: '18px', fontWeight: '900', color: '#4285f4' },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '20px',
  },
  dividerLine: { flex: 1, height: '1px', backgroundColor: '#ffffff15' },
  dividerText: { color: '#ffffff40', fontSize: '12px', whiteSpace: 'nowrap' },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    backgroundColor: '#0a1628',
    borderRadius: '14px',
    padding: '0 16px',
    marginBottom: '12px',
    border: '1px solid #00d4a030',
  },
  prefix: { color: '#00d4a0', fontSize: '16px', marginRight: '8px', fontWeight: '700' },
  input: {
    flex: 1,
    backgroundColor: 'transparent',
    border: 'none',
    color: '#ffffff',
    fontSize: '16px',
    padding: '14px 0',
    outline: 'none',
  },
  otpInput: {
    width: '100%',
    backgroundColor: '#0a1628',
    border: '1px solid #00d4a030',
    borderRadius: '14px',
    color: '#ffffff',
    fontSize: '28px',
    padding: '14px',
    textAlign: 'center',
    letterSpacing: '12px',
    marginBottom: '12px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  otpBtn: {
    width: '100%',
    background: 'linear-gradient(135deg, #00d4a0, #00a87d)',
    color: '#0a1628',
    border: 'none',
    borderRadius: '14px',
    padding: '16px',
    fontSize: '16px',
    fontWeight: '800',
    cursor: 'pointer',
    marginTop: '4px',
  },
  otpText: { color: '#ffffff60', fontSize: '13px', marginBottom: '12px', textAlign: 'center' },
  changeNumber: { color: '#00d4a0', cursor: 'pointer', marginTop: '12px', fontSize: '14px', textAlign: 'center' },
  error: { color: '#ff6b6b', fontSize: '13px', marginTop: '12px', textAlign: 'center' },
};